import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DynamicTextSkeleton from '../../components/DynamicTextSkeleton/DynamicTextSkeleton';
import { API_DOMAIN, CHINA_VERSION } from '../../globals';
import { setDynamicTranslation } from '../../redux/slices/appSettingsSlice/appSettingsSlice';
import useAxios from '../useAxios/useAxios';

const useTranslate = () => {
  const axios = useAxios();
  const dispatch = useDispatch();
  const currentLanguage = useSelector(
    (state) => state.appSettings.languages.current
  );

  // Track pending translations to avoid duplicate requests
  const [pendingTranslations, setPendingTranslations] = useState({});

  const staticTranslations = useSelector(
    (state) => state.appSettings.translations
  );

  const dynamicTranslations = useSelector(
    (state) => state.appSettings.dynamicTranslations
  );

  // Original translation function for static text
  const t = useCallback(
    (text) => staticTranslations?.[currentLanguage]?.[text] || text,
    [currentLanguage, staticTranslations]
  );

  // dynamic translation function that checks cache first
  const dt = useCallback(
    (fragments, variables, testId) => {
      if (!CHINA_VERSION || currentLanguage !== 'zh-cn') {
        const processedFragments = fragments.map((fragment) => {
          if (
            typeof fragment === 'string' &&
            RegExp(/^\{\w+\}$/).exec(fragment)
          ) {
            const key = fragment.substring(1, fragment.length - 1);
            return variables && variables[key] !== undefined
              ? variables[key]
              : fragment;
          }
          return fragment;
        });

        let finalText = '';
        for (let i = 0; i < processedFragments.length; i++) {
          const fragment = processedFragments[i];
          if (i === 0) {
            finalText = fragment;
            continue;
          }
          if (typeof fragment === 'string' && fragment.startsWith('-')) {
            finalText += fragment.substring(1);
          } else {
            finalText += ' ' + fragment;
          }
        }
        return finalText.trim();
      }

      // process each fragment, replacing placeholders with values
      const processedFragments = fragments.map((fragment) => {
        if (
          typeof fragment === 'string' &&
          RegExp(/^\{\w+\}$/).exec(fragment)
        ) {
          const key = fragment.substring(1, fragment.length - 1);
          return variables && variables[key] !== undefined
            ? variables[key]
            : fragment;
        }
        return fragment;
      });

      // join fragments with smart spacing logic
      let finalText = '';
      for (let i = 0; i < processedFragments.length; i++) {
        const fragment = processedFragments[i];
        if (i === 0) {
          finalText = fragment;
          continue;
        }
        if (typeof fragment === 'string' && fragment.startsWith('-')) {
          finalText += fragment.substring(1);
        } else {
          finalText += ' ' + fragment;
        }
      }

      const originalText = finalText.trim();

      // check if the translation is already cached in Redux
      if (dynamicTranslations?.[currentLanguage]?.[originalText]) {
        return (
          <DynamicTextSkeleton
            text={dynamicTranslations[currentLanguage][originalText]}
            isLoading={false}
            testId={testId}
          />
        );
      }

      if (!pendingTranslations[originalText]) {
        setPendingTranslations((prev) => ({ ...prev, [originalText]: true }));

        // fetch the translation
        axios
          .post(`${API_DOMAIN}/dynamic-translation`, { text: originalText })
          .then((response) => {
            if (response?.data?.text) {
              // store in redix
              dispatch(
                setDynamicTranslation({
                  language: currentLanguage,
                  original: originalText,
                  translation: response.data.text,
                })
              );
            }
          })
          .catch((error) => {
            console.error('Translation error:', error);
          })
          .finally(() => {
            // remove from pending list
            setPendingTranslations((prev) => {
              const newState = { ...prev };
              delete newState[originalText];
              return newState;
            });
          });
      }

      // return skeleton while translating
      return (
        <DynamicTextSkeleton
          text={originalText}
          isLoading={true}
          testId={testId}
        />
      );
    },
    [axios, currentLanguage, dispatch, dynamicTranslations, pendingTranslations]
  );

  // memoized constants using the translation function (unchanged)
  const constants = useMemo(
    () => ({
      errorMessage: `${t('Oops!')} ${t('Something went wrong.')} ${t(
        'Please refresh your browser or try again later.'
      )}`,
      prefixOptions: [
        { option: t('Dr.'), code: 'Dr' },
        { option: t('Prof.'), code: 'Prof' },
        { option: t('Miss.'), code: 'Miss' },
        { option: t('Mx.'), code: 'Mx' },
        { option: t('Mrs.'), code: 'Mrs' },
        { option: t('Ms.'), code: 'Ms' },
        { option: t('Mr.'), code: 'Mr' },
      ],
      formRules: {
        emailRule: {
          pattern: {
            message: t('Please provide a valid email address'),
            value:
              /^(?!.*\.\.)[A-Z0-9._%+-]+@[A-Z0-9-]+(?:\.[A-Z0-9]+)*\.[A-Z]{2,63}$/i,
          },
          required: t('Email is required'),
        },
      },
    }),
    [t]
  );

  return { t, dt, constants };
};

export default useTranslate;
